import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography, Paper, Button, TextField, Dialog, DialogActions, DialogContent, DialogTitle,
  CircularProgress, Alert, Box, Chip, IconButton, Tooltip, Grid, Avatar,
  Divider, Stack, Card, CardContent, InputAdornment, Tab, Tabs
} from '@mui/material';
import {
  Check, Close, Person, Phone, Badge, Home, Refresh, Search,
  Visibility, CreditCard, Description, DirectionsCar, HelpOutline, Info, History, ZoomIn, PhotoLibrary
} from '@mui/icons-material';
import { getImageUrl } from '../utils/imageUrl';

/* Clickable document image tile with lightbox support */
const DocImage = ({ src, label, onZoom }) => {
  if (!src) return (
    <Box sx={{ width: 130, height: 90, bgcolor: '#f1f5f9', borderRadius: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', border: '1px dashed #cbd5e1' }}>
      <Description sx={{ color: '#94a3b8', fontSize: 24 }} />
      <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: 0.5 }}>{label}</Typography>
      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>Missing</Typography>
    </Box>
  );

  const fullUrl = getImageUrl(src);

  return (
    <Tooltip title={`Click to zoom ${label}`}>
      <Box
        sx={{ position: 'relative', cursor: 'pointer', width: 130, '&:hover .zoom-overlay': { opacity: 1 } }}
        onClick={() => onZoom(fullUrl, label)}
      >
        <Box
          component="img"
          src={fullUrl}
          alt={label}
          sx={{
            width: 130, height: 90, objectFit: 'cover', borderRadius: 2,
            border: '1px solid #cbd5e1', display: 'block', transition: 'all 0.2s'
          }}
        />
        <Box className="zoom-overlay" sx={{
          position: 'absolute', top: 0, left: 0, width: 130, height: 90, borderRadius: 2,
          backgroundColor: 'rgba(0,0,0,0.4)', color: 'white', display: 'flex',
          alignItems: 'center', justifyContent: 'center', opacity: 0, transition: '0.2s opacity'
        }}>
          <ZoomIn />
        </Box>
        <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 0.5, color: '#64748b', fontWeight: 600 }} noWrap>
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const DriverApproval = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTab, setFilterTab] = useState('all');

  // Modal & Dialog state
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(''); // 'approve' | 'reject' | 'request_info'
  const [inputReason, setInputReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [detailDriver, setDetailDriver] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);

  useEffect(() => { fetchPendingDrivers(); }, []);

  const fetchPendingDrivers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/admin/pending-drivers');
      setDrivers(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load driver applications');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAction = (driver, type) => {
    setSelectedDriver(driver);
    setActionType(type);
    setInputReason('');
    setActionDialogOpen(true);
  };

  const handleExecuteAction = async () => {
    if (!selectedDriver) return;
    setProcessing(true);
    setError('');
    setSuccessMsg('');

    try {
      if (actionType === 'approve') {
        await axios.put(`/admin/approve-driver/${selectedDriver._id}`, { note: inputReason });
        setSuccessMsg(`Driver ${selectedDriver.name} approved successfully.`);
      } else if (actionType === 'reject') {
        if (!inputReason.trim()) {
          setError('Rejection reason is required.');
          setProcessing(false);
          return;
        }
        await axios.put(`/admin/reject-driver/${selectedDriver._id}`, { reason: inputReason });
        setSuccessMsg(`Driver ${selectedDriver.name} application rejected.`);
      } else if (actionType === 'request_info') {
        if (!inputReason.trim()) {
          setError('Details are required when requesting information.');
          setProcessing(false);
          return;
        }
        await axios.put(`/admin/request-info-driver/${selectedDriver._id}`, { details: inputReason });
        setSuccessMsg(`Information request sent to ${selectedDriver.name}.`);
      }

      setActionDialogOpen(false);
      setOpenDetailModal(false);
      fetchPendingDrivers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update driver status.');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusChip = (driver) => {
    const st = driver.status || (driver.isApproved ? 'active' : 'pending');
    switch (st) {
      case 'active':
        return <Chip label="Approved" color="success" size="small" icon={<Check />} />;
      case 'rejected':
        return <Chip label="Rejected" color="error" size="small" icon={<Close />} />;
      case 'info_requested':
        return <Chip label="Info Requested" color="warning" size="small" icon={<HelpOutline />} />;
      default:
        return <Chip label="Pending Review" color="info" size="small" icon={<Info />} />;
    }
  };

  const filteredDrivers = drivers.filter((driver) => {
    const st = driver.status || (driver.isApproved ? 'active' : 'pending');
    const matchesTab = filterTab === 'all' ? true : st === filterTab || (filterTab === 'pending' && st === 'pending');
    const matchesSearch =
      driver.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.phoneNumber?.includes(searchTerm) ||
      driver.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.vehicle?.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      driver.licenseNumber?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  return (
    <Box sx={{ p: 1 }}>
      {/* Header Banner */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold" sx={{ color: '#0f172a' }}>
            Driver Approval Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review driver identity documents, license cards, vehicle specifications, and execute verification actions.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchPendingDrivers} disabled={loading}>
          Refresh List
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {/* Search & Tabs */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Tabs
              value={filterTab}
              onChange={(e, val) => setFilterTab(val)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
            >
              <Tab label={`All (${drivers.length})`} value="all" />
              <Tab label={`Pending (${drivers.filter(d => d.status === 'pending' || (!d.status && !d.isApproved)).length})`} value="pending" />
              <Tab label={`Info Requested (${drivers.filter(d => d.status === 'info_requested').length})`} value="info_requested" />
              <Tab label={`Rejected (${drivers.filter(d => d.status === 'rejected').length})`} value="rejected" />
            </Tabs>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search driver by name, phone, license, vehicle number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Drivers List Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filteredDrivers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Person sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No Driver Applications Found</Typography>
          <Typography variant="body2" color="text.disabled">
            {searchTerm ? 'No driver applications matching your search.' : 'All driver verification requests have been processed.'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredDrivers.map((driver) => (
            <Grid item xs={12} key={driver._id}>
              <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                <Box sx={{ height: 4, bgcolor: driver.status === 'info_requested' ? '#f59e0b' : driver.status === 'rejected' ? '#ef4444' : '#0284c7' }} />
                <CardContent sx={{ p: 3 }}>
                  <Grid container spacing={3}>
                    {/* Left: Driver Profile & Contacts */}
                    <Grid item xs={12} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar
                          src={driver.profilePhoto ? getImageUrl(driver.profilePhoto) : ''}
                          sx={{ width: 56, height: 56, bgcolor: '#0284c7', fontWeight: 'bold' }}
                        >
                          {driver.name ? driver.name.charAt(0).toUpperCase() : 'D'}
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">{driver.name || 'Unnamed Driver'}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            ID: {driver._id?.slice(-8).toUpperCase()}
                          </Typography>
                          {getStatusChip(driver)}
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5 }} />

                      <Stack spacing={1}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone fontSize="small" color="action" /> {driver.phoneNumber || '—'}
                        </Typography>
                        {driver.email && (
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }} noWrap>
                            <CreditCard fontSize="small" color="action" /> {driver.email}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Badge fontSize="small" color="action" /> Lic: <strong>{driver.licenseNumber || '—'}</strong>
                        </Typography>
                        {driver.aadhar && (
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Person fontSize="small" color="action" /> Aadhar: {driver.aadhar}
                          </Typography>
                        )}
                        {driver.address && (
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }} noWrap>
                            <Home fontSize="small" color="action" /> {driver.address}
                          </Typography>
                        )}
                      </Stack>
                    </Grid>

                    {/* Center: Vehicle Details */}
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DirectionsCar fontSize="small" /> Registered Vehicle
                      </Typography>
                      {driver.vehicle?.type ? (
                        <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8fafc', borderRadius: 2 }}>
                          <Stack spacing={0.8}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary">
                              {driver.vehicle.type?.toUpperCase()} — {driver.vehicle.model || 'Standard'}
                            </Typography>
                            <Typography variant="body2"><strong>Reg No:</strong> {driver.vehicle.number || 'N/A'}</Typography>
                            <Typography variant="body2"><strong>Tank Capacity:</strong> {driver.vehicle.tankCapacity || '—'} L</Typography>
                            <Typography variant="body2"><strong>Base Rate:</strong> ₹{driver.vehicle.basePrice || '—'}</Typography>
                            <Typography variant="body2"><strong>Rate / 1000L:</strong> ₹{driver.vehicle.pricePer1000 || '—'}</Typography>
                          </Stack>
                        </Paper>
                      ) : (
                        <Alert severity="warning" variant="outlined">No vehicle linked</Alert>
                      )}

                      {driver.requestedInfoNote && (
                        <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, px: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                          <strong>Info Requested:</strong> {driver.requestedInfoNote}
                        </Alert>
                      )}
                      {driver.rejectionReason && (
                        <Alert severity="error" sx={{ mt: 1.5, py: 0.5, px: 1, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                          <strong>Rejection Reason:</strong> {driver.rejectionReason}
                        </Alert>
                      )}
                    </Grid>

                    {/* Right: Uploaded Documents */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhotoLibrary fontSize="small" /> Uploaded Documents & Photos
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <DocImage src={driver.licenseCardFront} label="License Front" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                        <DocImage src={driver.licenseCardBack} label="License Back" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                        <DocImage src={driver.vehicle?.photoFront} label="Vehicle Front" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                        <DocImage src={driver.vehicle?.photoBack} label="Vehicle Back" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                        <DocImage src={driver.vehicle?.photoSide} label="Vehicle Side" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                        <DocImage src={driver.vehicle?.vehicleRC} label="RC Book" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                        <DocImage src={driver.vehicle?.vehicleInsurance} label="Insurance" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  {/* Actions Footer */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => { setDetailDriver(driver); setOpenDetailModal(true); }}
                    >
                      Full Profile & History
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<Check />}
                      onClick={() => handleOpenAction(driver, 'approve')}
                    >
                      Approve Driver
                    </Button>
                    <Button
                      variant="outlined"
                      color="warning"
                      startIcon={<HelpOutline />}
                      onClick={() => handleOpenAction(driver, 'request_info')}
                    >
                      Request Info
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<Close />}
                      onClick={() => handleOpenAction(driver, 'reject')}
                    >
                      Reject Driver
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Full Profile & History Modal */}
      {detailDriver && (
        <Dialog open={openDetailModal} onClose={() => setOpenDetailModal(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar src={detailDriver.profilePhoto ? getImageUrl(detailDriver.profilePhoto) : ''} sx={{ bgcolor: '#0284c7' }}>
                {detailDriver.name?.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">{detailDriver.name}</Typography>
                <Typography variant="caption" color="text.secondary">License: {detailDriver.licenseNumber || 'N/A'}</Typography>
              </Box>
            </Box>
            {getStatusChip(detailDriver)}
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fafafa' }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>Personal Information</Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2"><b>Full Name:</b> {detailDriver.name || '—'}</Typography>
                    <Typography variant="body2"><b>Phone:</b> {detailDriver.phoneNumber || '—'}</Typography>
                    <Typography variant="body2"><b>Email:</b> {detailDriver.email || '—'}</Typography>
                    <Typography variant="body2"><b>DOB:</b> {detailDriver.dob || '—'}</Typography>
                    <Typography variant="body2"><b>Address:</b> {detailDriver.address || '—'}</Typography>
                    <Typography variant="body2"><b>Aadhar:</b> {detailDriver.aadhar || '—'}</Typography>
                    <Typography variant="body2"><b>PAN:</b> {detailDriver.pan || '—'}</Typography>
                    <Typography variant="body2"><b>License No:</b> {detailDriver.licenseNumber || '—'}</Typography>
                  </Stack>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8fafc' }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>Vehicle Information</Typography>
                  <Stack spacing={1}>
                    <Typography variant="body2"><b>Vehicle Type:</b> {detailDriver.vehicle?.type || '—'}</Typography>
                    <Typography variant="body2"><b>Model:</b> {detailDriver.vehicle?.model || '—'}</Typography>
                    <Typography variant="body2"><b>Vehicle Number:</b> {detailDriver.vehicle?.number || '—'}</Typography>
                    <Typography variant="body2"><b>Tank Capacity:</b> {detailDriver.vehicle?.tankCapacity || '—'} L</Typography>
                    <Typography variant="body2"><b>Base Price:</b> ₹{detailDriver.vehicle?.basePrice || '—'}</Typography>
                    <Typography variant="body2"><b>Price / 1000L:</b> ₹{detailDriver.vehicle?.pricePer1000 || '—'}</Typography>
                  </Stack>
                </Paper>
              </Grid>

              {/* All Documents */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>All Uploaded Documents</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <DocImage src={detailDriver.licenseCardFront} label="License Front" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                  <DocImage src={detailDriver.licenseCardBack} label="License Back" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                  <DocImage src={detailDriver.vehicle?.photoFront} label="Vehicle Front" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                  <DocImage src={detailDriver.vehicle?.photoBack} label="Vehicle Back" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                  <DocImage src={detailDriver.vehicle?.photoSide} label="Vehicle Side" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                  <DocImage src={detailDriver.vehicle?.vehicleRC} label="RC Book" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                  <DocImage src={detailDriver.vehicle?.vehicleInsurance} label="Insurance" onZoom={(url, label) => setZoomImage({ url, title: label })} />
                </Box>
              </Grid>

              {/* History Log */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <History fontSize="small" /> Verification History & Audit Log
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f8fafc' }}>
                  {detailDriver.approvalHistory && detailDriver.approvalHistory.length > 0 ? (
                    <Stack spacing={1.5}>
                      {detailDriver.approvalHistory.map((item, idx) => (
                        <Box key={idx} sx={{ p: 1.5, backgroundColor: 'white', borderRadius: 1, borderLeft: '4px solid #0284c7' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                              Action: {item.action?.replace('_', ' ')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{new Date(item.timestamp).toLocaleString()}</Typography>
                          </Box>
                          {item.reason && <Typography variant="body2" color="text.secondary">Note/Reason: {item.reason}</Typography>}
                          {item.requestedDetails && <Typography variant="body2" color="warning.main">Requested Details: {item.requestedDetails}</Typography>}
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      No prior approval history logged. Initial application review.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1, backgroundColor: '#f1f5f9' }}>
            <Button onClick={() => setOpenDetailModal(false)} color="inherit">Close</Button>
            <Button variant="outlined" color="warning" startIcon={<HelpOutline />} onClick={() => handleOpenAction(detailDriver, 'request_info')}>
              Request Information
            </Button>
            <Button variant="outlined" color="error" startIcon={<Close />} onClick={() => handleOpenAction(detailDriver, 'reject')}>
              Reject Driver
            </Button>
            <Button variant="contained" color="success" startIcon={<Check />} onClick={() => handleOpenAction(detailDriver, 'approve')}>
              Approve Driver
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Action Dialog (Approve / Reject / Request Info) */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' && 'Approve Driver Registration'}
          {actionType === 'reject' && 'Reject Driver Application'}
          {actionType === 'request_info' && 'Request Additional Information'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Driver: <strong>{selectedDriver?.name}</strong> ({selectedDriver?.phoneNumber})
          </Typography>

          {actionType === 'approve' && (
            <TextField
              fullWidth
              label="Approval Note (Optional)"
              placeholder="e.g. License and RC verified successfully"
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
            />
          )}

          {actionType === 'reject' && (
            <TextField
              fullWidth
              required
              label="Rejection Reason"
              placeholder="e.g. Expired license or invalid RC document image"
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
              error={!inputReason.trim()}
              helperText="Please state the exact reason for rejecting this application."
            />
          )}

          {actionType === 'request_info' && (
            <TextField
              fullWidth
              required
              label="Requested Information & Instructions"
              placeholder="e.g. Please re-upload a clear photograph of the back of your driving license."
              value={inputReason}
              onChange={(e) => setInputReason(e.target.value)}
              multiline
              rows={3}
              error={!inputReason.trim()}
              helperText="Specify what information or documents the driver must resubmit."
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setActionDialogOpen(false)} disabled={processing}>Cancel</Button>
          <Button
            variant="contained"
            color={actionType === 'approve' ? 'success' : actionType === 'reject' ? 'error' : 'warning'}
            onClick={handleExecuteAction}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={18} /> : actionType === 'approve' ? <Check /> : actionType === 'reject' ? <Close /> : <HelpOutline />}
          >
            Confirm {actionType.replace('_', ' ')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox Zoom */}
      {zoomImage && (
        <Dialog open={Boolean(zoomImage)} onClose={() => setZoomImage(null)} maxWidth="lg">
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {zoomImage.title}
            <IconButton onClick={() => setZoomImage(null)}><Close /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center', p: 1 }}>
            <img src={zoomImage.url} alt={zoomImage.title} style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: 8 }} />
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default DriverApproval;